<script setup lang="ts">
const { t } = useI18n()

const props = defineProps<{
  games: Game[]
  group: TournamentGroup
  tournament: Tournament
}>()

type PlayerData = {
  wins: number
  legs_won: number
  legs_lost: number
  legs_balance: number
  sets_balance: number
  id: number | null
  tie_bonus: number
  gid: number | null
  player: Player | null
}

const currentOrder = computed(() => {
  const baseData: PlayerData = {
    wins: 0,
    legs_won: 0,
    legs_lost: 0,
    legs_balance: 0,
    sets_balance: 0,
    id: null,
    tie_bonus: 0,
    gid: null,
    player: null,
  }

  let primarySort = null
  let secondarySort = null
  if (props.tournament.group_counting_type === 'won_legs') {
    primarySort = 'legs_won'
    secondarySort = 'legs_lost'
  }
  else if (props.tournament.group_counting_type === 'leg_balance') {
    primarySort = 'legs_balance'
  }
  else {
    return
  }

  const data: { [key: number]: PlayerData } = {}

  for (const game of props.games) {
    const player1 = game.player1
    const player2 = game.player2

    if (game.player1 === undefined || game.player2 === undefined) {
      continue
    }
    if (player1 === undefined || player2 === undefined) {
      continue
    }

    if (!data[player1.id]) {
      data[player1.id] = { ...baseData, player: player1 }
    }
    if (!data[player2.id]) {
      data[player2.id] = { ...baseData, player: player2 }
    }

    data[player1.id]!.gid = game.group
    data[player2.id]!.gid = game.group

    data[player1.id]!.id = player1.id
    data[player1.id]!.wins += game.winner_id === player1.id ? 1 : 0

    data[player1.id]!.legs_won += getPlayer(game.player1).score
    data[player1.id]!.legs_lost += getPlayer(game.player2).score
    data[player1.id]!.legs_balance += getPlayer(game.player1).score - getPlayer(game.player2).score

    // data[player1.id].sets_balance += game.player_1_sets - game.player_2_sets;

    data[player2.id]!.id = player2.id
    data[player2.id]!.wins += game.winner_id === player2.id ? 1 : 0

    data[player2.id]!.legs_won += getPlayer(game.player2).score
    data[player2.id]!.legs_lost += getPlayer(game.player1).score
    data[player2.id]!.legs_balance += getPlayer(game.player2).score - getPlayer(game.player1).score

    // data[player2.id].sets_balance += game.player_2_sets - game.player_1_sets;
  }

  const groupedData = Object.values(data).reduce((acc, item) => {
    if (secondarySort !== null) {
      const key = (item.wins * 1000000) + (item[`${primarySort}` as keyof PlayerData] as number * 10000 - (item[`${secondarySort}` as keyof PlayerData] as number * 100))
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    }
    else {
      const key = (item.wins * 1000000) + (item[`${primarySort}` as keyof PlayerData] as number * 10000)
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    }
  }, {} as { [key: number]: PlayerData[] })

  const finalData = Object.values(groupedData).map((group) => {
    if (group.length === 2) {
      const [player1, player2] = group
      const match = props.games.find(game =>
        (getPlayer(game.player1).id === player1!.id && getPlayer(game.player2).id === player2!.id)
        || (getPlayer(game.player2).id === player1!.id && getPlayer(game.player1).id === player2!.id),
      )

      if (match) {
        const winnerId = match.winner_id
        if (player1!.id == winnerId) {
          player1!.tie_bonus = 1
        }
        else if (player2!.id == winnerId) {
          player2!.tie_bonus = 1
        }
      }
    }

    if (group.length > 2) {
      if (props.group.shootout_outcome && props.group.shootout_outcome.length > 0) {
        let i = 3
        for (const id of props.group.shootout_outcome) {
          for (const item of group) {
            if (item.id == id) {
              item.tie_bonus = i
              i--
            }
          }
        }
      }
      else {
        // Handle the scenario for shootouts
        // throw new Error('ShootoutRequiredException');
      }
    }

    return group
  })

  let increment = 1
  const flatten: { [key: number]: PlayerData } = {}
  for (const group of finalData) {
    for (const player of group) {
      let weight = (player.wins * 10000000) + (player[`${primarySort}` as keyof PlayerData] as number * 10000)

      if (secondarySort !== null) {
        weight -= (player[`${secondarySort}` as keyof PlayerData] as number * 10)
      }

      weight += player.tie_bonus
      if (!flatten[weight]) {
        flatten[weight] = player
      }
      else {
        flatten[weight + increment++] = player
      }
    }
  }

  const sortedKeys = Object.keys(flatten).sort((a: string, b: string) => parseInt(a) - parseInt(b))

  const sortedFlatten: PlayerData[] = []
  sortedKeys.reverse().forEach((key) => {
    sortedFlatten.push(flatten[key as unknown as number]!)
  })

  const midIndex = Math.ceil(sortedFlatten.length / 2)
  const firstHalf = sortedFlatten.slice(0, midIndex)
  const secondHalf = sortedFlatten.slice(midIndex)

  return [firstHalf, secondHalf]
})

const orderInfo = computed(() => {
  if (props.tournament.group_counting_type === 'won_legs') {
    return [t('Wins'), t('Legs won'), t('Legs lost'), t('Individual duel')]
  }

  return [t('Wins'), t('Legs balance'), t('Individual duel')]
})

const headers = computed(() => {
  if (props.tournament.group_counting_type === 'won_legs') {
    return [t('Player'), t('Wins'), t('Legs'), t('Tie bonus')]
  }

  return [t('Player'), t('Wins'), t('Legs'), t('Legs balance'), t('Tie bonus')]
})
</script>

<template>
  <div class="max-w-full overflow-auto">
    <UiAlert
      v-if="group.shootout_required && group.shootout_outcome.length === 0"
      variant="warning"
      :title="$t('Shootout required')"
    />
    <UiTable>
      <UiTableCaption>
        <div class="text-xs flex flex-row gap-2">
          {{ $t('Order info:') }}
          <div
            v-for="(info, i) in orderInfo"
            :key="info"
          >
            {{ i + 1 }}. {{ info }}
          </div>
        </div>
        <div class="col-span-5 text-xs">
          *Tie bonus is awarded for the player who wins the shootout or individual duel
        </div>
      </UiTableCaption>
      <UiTableHeader>
        <UiTableRow>
          <UiTableHead
            v-for="header in headers"
            :key="header"
          >
            {{ header }}
          </UiTableHead>
        </UiTableRow>
      </UiTableHeader>
      <UiTableBody>
        <UiTableRow
          v-for="(player, i) in currentOrder?.[0] ?? []"
          :key="player.player?.id"
          class="bg-green-100 dark:bg-green-900"
        >
          <UiTableCell class="truncate p-1">
            {{ i+1 }} {{ player.player?.name }}
          </UiTableCell>
          <UiTableCell class="p-1">
            {{ player.wins }}
          </UiTableCell>
          <UiTableCell class="p-1">
            <UiBadge
              variant="success"
              class="mr-px"
            >
              {{ player.legs_won }}
            </UiBadge>
            <UiBadge variant="warning">
              {{ player.legs_lost }}
            </UiBadge>
          </UiTableCell>
          <UiTableCell
            v-if="tournament.group_counting_type === 'leg_balance'"
            class="p-1"
          >
            {{ player.legs_balance }}
          </UiTableCell>
          <UiTableCell class="p-1">
            {{ player.tie_bonus }}
          </UiTableCell>
        </UiTableRow>
        <UiTableRow
          v-for="(player, i) in currentOrder?.[1] ?? []"
          :key="player.player?.id"
          class="bg-yellow-100 dark:bg-yellow-900"
        >
          <UiTableCell class="p-1">
            {{ i+3 }} {{ player.player?.name }}
          </UiTableCell>
          <UiTableCell class="p-1">
            {{ player.wins }}
          </UiTableCell>
          <UiTableCell class="p-1">
            <UiBadge
              variant="success"
              class="mr-px"
            >
              {{ player.legs_won }}
            </UiBadge>
            <UiBadge variant="warning">
              {{ player.legs_lost }}
            </UiBadge>
          </UiTableCell>
          <UiTableCell
            v-if="tournament.group_counting_type === 'leg_balance'"
            class="p-1"
          >
            {{ player.legs_balance }}
          </UiTableCell>
          <UiTableCell class="p-1">
            {{ player.tie_bonus }}
          </UiTableCell>
        </UiTableRow>
      </UiTableBody>
    </UiTable>
  </div>
</template>
